import { Table, TableColumnType, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { RobotContributionMsgReplyReplyDetailDay } from 'services/api/dashboard';
import { useTranslate } from '@tolgee/react';
import {
  millisecondDurationDisplay,
  numDisplay,
  percentDisplay,
} from 'views/Dashboard/ImplementData/utils';
import { useMemo } from 'react';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';

interface ReplyDetailProps {
  dataSource: RobotContributionMsgReplyReplyDetailDay[];
}

// 回复明细
export const ReplyDetail = (props: ReplyDetailProps) => {
  const { dataSource } = props;
  const { t } = useTranslate();
  const percentRender = (value: number) => percentDisplay(value, 2);

  const { whiteListConfig: { PRE_RECEPTION_LIST } = {} } = useOrgConfigStateValue();

  const columns: TableColumnType<any>[] = [
    {
      title: t('日期'),
      dataIndex: 'dt',
      key: 'dt',
      width: 120,
      fixed: 'left',
    },
    {
      title: (
        <Tooltip title={t('发送过消息的买家数')}>
          {t('咨询买家数')}
          <QuestionCircleOutlined className="ml-1" />
        </Tooltip>
      ),
      dataIndex: 'buyerCount',
      key: 'buyerCount',
      width: 160,
      render: numDisplay,
    },
    {
      title: (
        <Tooltip title={t('买家侧消息总数')}>
          {t('买家消息数')}
          <QuestionCircleOutlined className="ml-1" />
        </Tooltip>
      ),
      dataIndex: 'queryCount',
      key: 'queryCount',
      width: 160,
      render: numDisplay,
    },
    {
      title: (
        <Tooltip title={t('24小时内收到卖家侧回复的买家消息数')}>
          {t('回复消息数')}
          <QuestionCircleOutlined className="ml-1" />
        </Tooltip>
      ),
      dataIndex: 'replyCount',
      key: 'replyCount',
      width: 160,
      render: numDisplay,
    },
    {
      title: (
        <Tooltip title={t('24小时内回复，且首次回复来自人工客服的买家消息数')}>
          {t('回复消息数-人工客服')}
          <QuestionCircleOutlined className="ml-1" />
        </Tooltip>
      ),
      dataIndex: 'humanReplyCount',
      key: 'humanReplyCount',
      width: 160,
      render: numDisplay,
    },
    {
      title: (
        <Tooltip title={t('24小时内回复，且首次回复来自机器人使用 AI 能力的买家消息数')}>
          {t('回复消息数-智能机器人')}
          <QuestionCircleOutlined className="ml-1" />
        </Tooltip>
      ),
      dataIndex: 'aiReplyCount',
      key: 'aiReplyCount',
      width: 160,
      render: numDisplay,
    },
    {
      title: (
        <Tooltip title={t('24小时内回复的买家消息数/买家消息总数')}>
          {t('24小时回复率')}
          <QuestionCircleOutlined className="ml-1" />
        </Tooltip>
      ),
      dataIndex: 'replyRate',
      key: 'replyRate',
      width: 160,
      render: percentRender,
    },
    {
      title: (
        <Tooltip title={t('24小时内首次回复来自人工客服的买家消息数/买家消息总数')}>
          {t('24小时回复率-人工客服')}
          <QuestionCircleOutlined className="ml-1" />
        </Tooltip>
      ),
      dataIndex: 'humanReplyRate',
      key: 'humanReplyRate',
      width: 160,
      render: percentRender,
    },
    {
      title: (
        <Tooltip title={t('24小时内首次回复来自机器人使用 AI 能力的买家消息数/买家消息总数')}>
          {t('24小时回复率-智能机器人')}
          <QuestionCircleOutlined className="ml-1" />
        </Tooltip>
      ),
      dataIndex: 'aiReplyRate',
      key: 'aiReplyRate',
      width: 160,
      render: percentRender,
    },
    {
      title: (
        <Tooltip title={t('卖家侧24小时内回复的买家消息，首次响应的平均时长')}>
          {t('平均响应时长')}
          <QuestionCircleOutlined className="ml-1" />
        </Tooltip>
      ),
      dataIndex: 'replyTime',
      key: 'replyTime',
      width: 160,
      render: millisecondDurationDisplay,
    },
    {
      title: (
        <Tooltip title={t('24小时内首次回复来自人工客服的买家消息，首次响应的平均时长')}>
          {t('平均响应时长-人工客服')}
          <QuestionCircleOutlined className="ml-1" />
        </Tooltip>
      ),
      dataIndex: 'humanReplyTime',
      key: 'humanReplyTime',
      width: 160,
      render: millisecondDurationDisplay,
    },
    {
      title: (
        <Tooltip title={t('24小时内首次回复来自机器人使用 AI 能力的买家消息，首次响应的平均时长')}>
          {t('平均响应时长-智能机器人')}
          <QuestionCircleOutlined className="ml-1" />
        </Tooltip>
      ),
      dataIndex: 'aiReplyTime',
      key: 'aiReplyTime',
      width: 160,
      render: millisecondDurationDisplay,
    },
    {
      title: (
        <Tooltip title={t('所有问题均由AI完成接待的买家数量')}>
          {t('AI独立接待买家数')}
          <QuestionCircleOutlined className="ml-1" />
        </Tooltip>
      ),
      key: 'aiAloneReceptionBuyerCount',
      dataIndex: 'aiAloneReceptionBuyerCount',
      width: 160,
      render: numDisplay,
    },
    {
      title: (
        <Tooltip title={t('AI独立接待买家数/咨询买家数')}>
          {t('AI独立接待买家率')}
          <QuestionCircleOutlined className="ml-1" />
        </Tooltip>
      ),
      key: 'aiAloneReceptionBuyerRate',
      dataIndex: 'aiAloneReceptionBuyerRate',
      width: 160,
      render: percentRender,
    },
    ...(PRE_RECEPTION_LIST
      ? [
          {
            title: (
              <Tooltip title={t('店铺所在时区当日由买家发起的Session总数')}>
                {t('买家发起 session 数')}
                <QuestionCircleOutlined className="ml-1" />
              </Tooltip>
            ),
            key: 'totalReceptionCount',
            dataIndex: 'totalReceptionCount',
            width: 160,
            render: numDisplay,
          },
          {
            title: (
              <Tooltip
                title={t(
                  '当日由买家发起的Session中，均由AI独立完成接待的数量（当日未关闭的统计至次日03:00）',
                )}
              >
                {t('AI独立接待Session数')}
                <QuestionCircleOutlined className="ml-1" />
              </Tooltip>
            ),
            key: 'aiAloneReceptionCount',
            dataIndex: 'aiAloneReceptionCount',
            width: 160,
            render: numDisplay,
          },
          {
            title: (
              <Tooltip title={`${t('AI独立接待Session数')} / ${t('买家发起 session 数')}`}>
                {t('AI独立接待率')}
                <QuestionCircleOutlined className="ml-1" />
              </Tooltip>
            ),
            key: 'aiAloneReceptionRate',
            dataIndex: 'aiAloneReceptionRate',
            width: 160,
            render: percentRender,
          },
        ]
      : []),
  ];

  const data = useMemo(() => [...dataSource].reverse(), [dataSource]);

  return (
    <Table
      scroll={{ x: '100%', y: '420px' }}
      columns={columns}
      dataSource={data}
      pagination={false}
    />
  );
};
